import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { WireCasePage } from '../_shared/types';
import { toCasePage } from '../_shared/types';

/**
 * The case queue — one list holding all five types, ordered by last activity.
 *
 * **Two parameters are sent: the cursor and the reference.** The endpoint also
 * accepts a type facet, a state facet, a raiser, a subject owner and a page size,
 * and this surface asks for none of those: the catalog's own row says the queue
 * runs «across open, awaiting user, resolved, and closed» — one list covering
 * four states, not a control for choosing among them — and no row gives this
 * queue a sort or a filter strip. The page size stays the service's, so a
 * server-owned number does not end up behind a rebuild of this bundle.
 *
 * **`caseReference` is an equality lookup, not a search**, and the service says
 * so in its own filter model. A case has no title — a reason from a configured
 * list plus free-text detail, and nothing else nameable — so the reference is
 * the token the subscriber and the operator quote at each other, and pasting it
 * is how an operator reaches the case being discussed. It is the only field on
 * the row that can be matched exactly; a term over reasons would return the
 * cases that share a configured reason, which is not the question being asked.
 *
 * A lookup replaces the walk rather than narrowing it: the cursor is dropped
 * while a reference is set, because a keyset cursor names a position in the
 * unfiltered queue.
 *
 * The cursor **is** sent, because the queue is worked rather than skimmed: the
 * service pages by keyset at ~1 500 new cases a month, and a surface that reads
 * only the first page leaves every case behind it unreachable — a row nobody can
 * reach is a case nobody works.
 */
export function caseQueueKey(cursor: string | null, reference = '') {
  return key('support', 'cases', { cursor, reference });
}

export function useCaseQueue(cursor: string | null, reference = '') {
  const trimmed = reference.trim();

  return useQuery({
    queryKey: caseQueueKey(trimmed ? null : cursor, trimmed),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (trimmed) params.set('caseReference', trimmed);
      else if (cursor) params.set('cursor', cursor);
      const search = params.toString();
      return toCasePage(await api.get<WireCasePage>('support', `/admin/cases${search ? `?${search}` : ''}`));
    },
    // A queue's depth is what an operator came back to the tab for.
    staleTime: STALE_TIME.list,
    // Walking a page keeps the rows in place while the next set loads, so the
    // table does not blink through a skeleton on every press.
    placeholderData: keepPreviousData,
  });
}
